import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import Loader from '@amzn/meridian/loader'
import userToken from '@amzn/meridian-tokens/base/icon/user'
import chevronLeftSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small'
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium'

import { SettingsMenuItem } from './components'
import { LegalLinks } from './components/LegalLinks'
import { NotificationSettings } from './NotificationSettings'
import { PushNotificationSettings } from './PushNotificationSettings'
import { PreferencesService, INotificationPreferences } from './services/PreferencesService'
import { authService } from '@services/auth/AuthService'
import { useLogout } from '@hooks/useLogout'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { getCurrentCountryCode, getStoreId } from '@atredux/selectors/storeSelector'
import { PROFILE_SCREENS, ProfileScreen, IUserProfile } from './constants/profileSettings'
import { resolveDisplayRole, GENERIC_ROLE_KEY } from './utils/roleDisplay'
import './ProfileSettingsPage.scss'

/**
 * Profile & Settings Page
 *
 * Main entry point for the profile settings flow. Renders as a bottom sheet
 * (via ProfileSettingsSheet) following the NotificationsSheet pattern.
 */
export const ProfileSettingsPage: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
    const [currentScreen, setCurrentScreen] = useState<ProfileScreen>(PROFILE_SCREENS.PROFILE)
    const [profile, setProfile] = useState<IUserProfile>({ name: '', email: '', role: undefined })
    const [preferences, setPreferences] = useState<INotificationPreferences | null>(null)
    // isLoading gates ONLY the profile identity (name/role) — the data every
    // screen here needs. Logout needs no data at all, so it is never gated.
    const [isLoading, setIsLoading] = useState(true)
    // Notification preferences are loaded separately and are non-fatal: a
    // Gateway/NS failure must not hide the profile or the Sign Out button, so
    // its error is scoped to the Notification Settings sub-screen. See
    // P536181800 (new UX hid logout in prod-IAD when GetNotificationPreferences
    // returned InternalServerException).
    const [preferencesLoading, setPreferencesLoading] = useState(true)
    const [preferencesError, setPreferencesError] = useState(false)
    const isMountedRef = useRef(true)
    const logout = useLogout()
    const [bundle] = useBundle(BUNDLE_NAME)
    const storeId = useSelector(getStoreId)
    const countryCode = useSelector(getCurrentCountryCode)

    // Loads notification preferences in isolation. NON-FATAL by contract: any
    // failure sets preferencesError (scoped to the Notification Settings
    // sub-screen) and never touches isLoading, so the profile and Sign Out
    // button stay rendered. See P536181800.
    const loadPreferences = useCallback(async () => {
        setPreferencesLoading(true)
        setPreferencesError(false)
        try {
            const prefs = await PreferencesService.getPreferences()
            if (isMountedRef.current) {
                setPreferences(prefs)
            }
        } catch (error) {
            console.error('[ProfileSettingsPage] Failed to load notification preferences:', error)
            if (isMountedRef.current) {
                setPreferences(null)
                setPreferencesError(true)
            }
        } finally {
            if (isMountedRef.current) setPreferencesLoading(false)
        }
    }, [])

    const loadData = useCallback(async () => {
        setIsLoading(true)

        // Profile identity + role gate the page. Both resolve defensively
        // (getUserProfile() returns empty strings on error; resolveDisplayRole()
        // catches internally), so this should not reject — but guard anyway so a
        // future regression can never leave the page stuck on the loader or hide
        // the Sign Out button.
        try {
            const [userProfile, roleKey] = await Promise.all([
                authService.getUserProfile(),
                resolveDisplayRole(storeId),
            ])
            if (isMountedRef.current) {
                setProfile({ name: userProfile.name, email: userProfile.email, role: roleKey })
            }
        } catch (error) {
            console.error('[ProfileSettingsPage] Failed to load profile identity:', error)
            if (isMountedRef.current) {
                setProfile({ name: '', email: '', role: undefined })
            }
        } finally {
            if (isMountedRef.current) setIsLoading(false)
        }

        // Separate, non-fatal: never blocks the profile or logout.
        await loadPreferences()
    }, [storeId, loadPreferences])

    useEffect(() => {
        isMountedRef.current = true
        loadData()
        return () => { isMountedRef.current = false }
    }, [loadData])

    const handleRetryPreferences = useCallback(() => {
        loadPreferences()
    }, [loadPreferences])

    const handleUpdatePreferences = useCallback(async (updates: Partial<INotificationPreferences>) => {
        if (!preferences) return

        // Capture prior values of only the keys being changed for a targeted revert.
        const prevSlice = Object.fromEntries(
            (Object.keys(updates) as Array<keyof INotificationPreferences>).map((k) => [k, preferences[k]]),
        )

        // Optimistic update using functional form — safe against concurrent toggles
        setPreferences((prev) => (prev ? { ...prev, ...updates } : prev))

        try {
            const updated = await PreferencesService.updatePreferences(updates)
            setPreferences((prev) => {
                if (!prev) return prev
                const merged = { ...prev }
                for (const k of Object.keys(updates) as Array<keyof INotificationPreferences>) {
                    merged[k] = updated[k]
                }
                return merged
            })
        } catch (error) {
            console.error('[ProfileSettingsPage] Failed to update preferences:', error)
            // Revert only the affected keys, leaving concurrent updates intact.
            setPreferences((prev) => (prev ? { ...prev, ...prevSlice } : prev))
        }
    }, [preferences])

    const handleBack = () => {
        switch (currentScreen) {
        case PROFILE_SCREENS.PUSH:
            setCurrentScreen(PROFILE_SCREENS.NOTIFICATION_SETTINGS)
            break
        case PROFILE_SCREENS.NOTIFICATION_SETTINGS:
            setCurrentScreen(PROFILE_SCREENS.PROFILE)
            break
        default:
            break
        }
    }

    const renderBackButton = () => {
        return (
            <Row alignmentVertical="center" widths={['fit', 'fill', 'fit']} spacingInset="300 none">
                {currentScreen !== PROFILE_SCREENS.PROFILE ? (
                    <Button type="link" onClick={handleBack} data-testid="back-button">
                        <Row alignmentVertical="center" spacing="100">
                            <Icon tokens={chevronLeftSmallTokens} />
                            <Text type="b300">{t(bundle, 'jsma-web-profile-back')}</Text>
                        </Row>
                    </Button>
                ) : <div />}
                <div />
                {onClose && (
                    <Button type="icon" onClick={onClose} data-testid="profile-close-button">
                        <Icon tokens={closeMediumTokens} />
                    </Button>
                )}
            </Row>
        )
    }

    if (isLoading) {
        return (
            <Column alignmentHorizontal="center" spacingInset="600 400">
                <Loader />
                <Text>{t(bundle, 'jsma-web-profile-loading')}</Text>
            </Column>
        )
    }

    // A loader shown inside the Notification Settings / Push sub-screens while
    // preferences are still being fetched (the profile screen renders without
    // them).
    const renderPreferencesLoading = () => (
        <Column alignmentHorizontal="center" spacingInset="600 400">
            <Loader />
        </Column>
    )

    // Scoped error for the Notification Settings / Push sub-screens when
    // preferences fail to load. Keeps the profile screen and Sign Out intact;
    // the user can retry just the preferences fetch.
    const renderPreferencesError = () => (
        <Column alignmentHorizontal="center" spacing="200" spacingInset="600 400">
            <Text>{t(bundle, 'jsma-web-profile-notificationsLoadError')}</Text>
            <Button type="secondary" onClick={handleRetryPreferences} data-testid="notifications-retry-button">
                {t(bundle, 'jsma-web-profile-retry')}
            </Button>
        </Column>
    )

    const renderScreen = () => {
        switch (currentScreen) {
        case PROFILE_SCREENS.NOTIFICATION_SETTINGS:
            if (preferencesLoading) return renderPreferencesLoading()
            if (preferencesError || !preferences) return renderPreferencesError()
            return (
                <NotificationSettings
                    preferences={preferences}
                    onUpdate={handleUpdatePreferences}
                    onNavigate={(subPage) => setCurrentScreen(subPage)}
                />
            )
        case PROFILE_SCREENS.PUSH:
            if (preferencesLoading) return renderPreferencesLoading()
            if (preferencesError || !preferences) return renderPreferencesError()
            return (
                <PushNotificationSettings
                    preferences={preferences}
                    onUpdate={handleUpdatePreferences}
                />
            )
        case PROFILE_SCREENS.PROFILE:
        default:
            return (
                    <Column spacing="400" spacingInset="400">
                        {/* User Avatar & Info */}
                        <Column alignmentHorizontal="center" spacing="200" spacingInset="400 none">
                            <div className="profile-avatar">
                                <Icon tokens={userToken} />
                            </div>
                            <Text type="h300">{profile.name || t(bundle, 'jsma-web-profile-defaultName')}</Text>
                            <Row alignmentVertical="center" spacing="200">
                                <div className="profile-role-dot" />
                                <Text type="b200" color="secondary">{t(bundle, profile.role ?? GENERIC_ROLE_KEY)}</Text>
                            </Row>
                        </Column>

                        {/* Menu Items */}
                        <Column spacing="none">
                            <SettingsMenuItem
                                label={t(bundle, 'jsma-web-profile-notificationSettings')}
                                onClick={() => setCurrentScreen(PROFILE_SCREENS.NOTIFICATION_SETTINGS)}
                                testId="menu-notification-settings"
                            />
                        </Column>

                        {/* Sign Out */}
                        <Column spacingInset="400 none">
                            <Button
                                type="secondary"
                                onClick={logout}
                                data-testid="profile-sign-out"
                            >
                                {t(bundle, 'jsma-web-common-signOut')}
                            </Button>
                        </Column>

                        {/* Required by JWO legal; the site follows the store's country. */}
                        <LegalLinks countryCode={countryCode} />
                    </Column>
            )
        }
    }

    return (
        <div className="profile-settings-page" data-testid="profile-settings-page">
            <Column spacing="none">
                {renderBackButton()}
                {renderScreen()}
            </Column>
        </div>
    )
}

export default ProfileSettingsPage
